/**
 * Author: Claude Code
 * Generation: Global Phaser preload service for animation assets
 * Version: v3.2.44
 * Modified: 2025-11-04
 */

import * as Phaser from 'phaser';
import { SpriteAssetLoader } from '@game/animation/SpriteAssetLoader';
import { AnimationType, CharacterType } from '@game/animation/spriteTypes';
import { colorToCharacterType, getCharacterFrameKey } from '@game/animation/utils/spriteKeyUtils';

// Shared preload scene for all agent previews
class GlobalPreloadScene extends Phaser.Scene {
  private loader: SpriteAssetLoader | null = null;
  private isReady = false;

  constructor() {
    super('GlobalPreloadScene');
  }

  preload() {
    // Configure pixel art settings for better sprite rendering
    try { this.game.config.pixelArt = true as any; } catch {}
    try { (this.cameras?.main as any).roundPixels = true; } catch {}

    this.loader = new SpriteAssetLoader(this);

    // Preload IDLE animation for all 7 character types in parallel
    const characters = [
      CharacterType.GREEN,
      CharacterType.RED,
      CharacterType.PURPLE,
      CharacterType.BLUE,
      CharacterType.WHITE,
      CharacterType.BLACK,
      CharacterType.ORANGE
    ];

    // Queue all IDLE animations for batch loading
    characters.forEach(charType => {
      this.loader!.loadCharacterAnimation(charType, AnimationType.IDLE);
    });
  }

  create() {
    // Mark all animations as loaded after Phaser completes loading
    if (this.loader) {
      const characters = [
        CharacterType.GREEN, CharacterType.RED, CharacterType.PURPLE,
        CharacterType.BLUE, CharacterType.WHITE, CharacterType.BLACK,
        CharacterType.ORANGE
      ];

      // Create animation definitions for all character types
      characters.forEach(charType => {
        const frames: { key: string; frame: any }[] = [];
        for (let i = 0; i < 11; i++) {
          frames.push({ key: getCharacterFrameKey(charType, AnimationType.IDLE, i), frame: null });
        }
        const animKey = `global_${charType}_idle`;
        if (!this.anims.exists(animKey)) {
          this.anims.create({ key: animKey, frames, frameRate: 10, repeat: -1 });
        }
      });
    }
    this.isReady = true;
  }

  // Update preview sprite for a specific color (no texture reloading)
  updatePreviewSprite(containerId: string, color: string, size: number = 128): Phaser.GameObjects.Sprite | null {
    if (!this.isReady) return null;

    const charType = colorToCharacterType(color) as unknown as CharacterType;
    const firstKey = getCharacterFrameKey(charType, AnimationType.IDLE, 0);

    // Create sprite from already-loaded textures
    const sprite = this.add.sprite(size / 2, size / 2, firstKey);
    const SCALE = Math.min((size * 0.9) / 48, 2);
    sprite.setScale(SCALE);
    try { (sprite as any).setOrigin?.(0.5, 0.5); } catch {}

    // Play animation
    const animKey = `global_${charType}_idle`;
    try { (sprite as any).anims?.play(animKey); } catch {}

    return sprite;
  }

  getIsReady(): boolean {
    return this.isReady;
  }
}

// Singleton service for managing global Phaser preloading
export class PhaserPreloadService {
  private static instance: Phaser.Game | null = null;
  private static scene: GlobalPreloadScene | null = null;
  private static containerElement: HTMLDivElement | null = null;
  private static isInitialized = false;

  /**
   * Initialize the global Phaser instance and preload all animations
   * Call this ONCE at app startup in main.tsx
   */
  static async initialize(): Promise<void> {
    if (this.isInitialized) return;
    this.isInitialized = true;

    // Create invisible container for Phaser
    const container = document.createElement('div');
    container.id = 'phaser-preload-container';
    container.style.position = 'fixed';
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.visibility = 'hidden';
    document.body.appendChild(container);
    this.containerElement = container;

    // Create persistent Phaser game instance
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: 1,
      height: 1,
      backgroundColor: 'rgba(0,0,0,0)',
      parent: container,
      pixelArt: true,
      render: { pixelArt: true, antialias: false, transparent: true },
      scene: GlobalPreloadScene
    };

    this.instance = new Phaser.Game(config);

    // Wait for scene to be created and ready
    await new Promise<void>(resolve => {
      const checkReady = setInterval(() => {
        const scene = this.instance?.scene.getScene('GlobalPreloadScene') as GlobalPreloadScene | undefined;
        if (scene?.getIsReady?.()) {
          this.scene = scene;
          clearInterval(checkReady);
          resolve();
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => {
        clearInterval(checkReady);
        console.warn('PhaserPreloadService: Timeout waiting for preload to complete');
        resolve();
      }, 5000);
    });
  }

  /**
   * Get the global Phaser game instance
   */
  static getInstance(): Phaser.Game | null {
    return this.instance;
  }

  /**
   * Get the global preload scene
   */
  static getScene(): GlobalPreloadScene | null {
    return this.scene;
  }

  /**
   * Check if preloading is complete
   */
  static isReady(): boolean {
    return this.isInitialized && this.scene?.getIsReady?.() === true;
  }

  /**
   * Cleanup resources (called on app shutdown)
   */
  static destroy(): void {
    if (this.instance) {
      try {
        this.instance.destroy(true);
      } catch (e) {
        console.warn('Error destroying Phaser instance:', e);
      }
      this.instance = null;
    }
    this.scene = null;

    if (this.containerElement && this.containerElement.parentNode) {
      this.containerElement.parentNode.removeChild(this.containerElement);
    }
    this.containerElement = null;
    this.isInitialized = false;
  }
}
